import { useState, useEffect, useCallback } from "react";
import { Loader2, Images, Clapperboard, Music4, Sparkles, X, Play, Globe, ArrowRight, User, FolderPlus, Folder, Check, Wand2, Pencil, Trash2, ChevronDown, CheckSquare, Square, Star, FolderInput, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { mistApi, streamAsset } from "@/api/mist";
import { useFounderGate } from "@/lib/useFounderGate";

const TABS = [
  { key: "images", label: "Images", track: "image", Icon: Images, accent: "#38BDF8" },
  { key: "audio", label: "Music", track: "music", Icon: Music4, accent: "#00E5C0" },
];

// Track-appropriate blueprint album labels (Option 4b — same logic, tuned labels).
const BLUEPRINT_LABELS = {
  image: { character: "Character Fronts", scene: "Scene Layouts" },
  video: { character: "Character Clips", scene: "Scene Clips" },
  music: { character: "Character Themes", scene: "Scene Themes" },
};

/* Streaming thumbnail — pulls the shared asset's bytes as a blob URL. */
function CommThumb({ item }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url, alive = true;
    const id = item.track === "music" ? (item.cover_asset_id || item.id) : item.id;
    streamAsset(id).then((u) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [item.id]);
  if (item.track === "music" && !item.cover_asset_id) {
    return <div className="w-full h-full grid place-items-center bg-black"><Music4 size={22} className="text-[#00E5C0]" /></div>;
  }
  if (!src) return <div className="w-full h-full grid place-items-center bg-black/60"><Loader2 size={16} className="text-white/40 animate-spin" /></div>;
  if (item.track === "video") return <video src={src} className="w-full h-full object-cover" muted playsInline />;
  return <img src={src} alt={item.title || "shared"} className="w-full h-full object-cover" />;
}

/* Fullscreen preview + "Use in a creation". No download anywhere. */
function PreviewModal({ item, onClose, onUse, using, isFounder, onRename, onDelete, onMove, onGenerateCover }) {
  const [src, setSrc] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  useEffect(() => {
    let url, alive = true;
    streamAsset(item.id).then((u) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [item.id]);
  return (
    <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col" data-testid="community-preview">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="min-w-0">
          <p className="font-serif text-lg text-white truncate">{item.title || item.filename}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/45">by @{item.by} · {item.genre}</p>
        </div>
        <button onClick={onClose} data-testid="community-preview-close" className="w-9 h-9 grid place-items-center border border-white/30 rounded-full text-white"><X size={18} /></button>
      </div>
      <div className="flex-1 min-h-0 grid place-items-center p-4">
        {!src ? <Loader2 size={26} className="text-white/40 animate-spin" /> :
          item.track === "video" ? <video src={src} controls autoPlay className="max-w-full max-h-full" /> :
          item.track === "music" ? <audio src={src} controls autoPlay className="w-full" /> :
          <img src={src} alt={item.title} className="max-w-full max-h-full object-contain" />}
      </div>
      <div className="shrink-0 p-4 border-t border-white/10">
        <button onClick={onUse} disabled={using} data-testid="community-use-btn"
          className="w-full flex items-center justify-center gap-2 bg-gold text-black rounded-xl py-3.5 font-mono text-[11px] font-black uppercase tracking-widest active:scale-[0.99] disabled:opacity-60">
          {using ? <Loader2 size={15} className="animate-spin" /> : <><Sparkles size={15} /> Use in a creation <ArrowRight size={14} /></>}
        </button>
        <p className="mt-2 text-center font-mono text-[8px] uppercase tracking-widest text-white/35">Adds a remixable copy to your library · no device download</p>
        {isFounder && (
          <div className="space-y-2 mt-3 pt-3 border-t border-white/10" data-testid="community-admin-controls">
            {item.track === "music" && !item.cover_asset_id && (
              <button
                onClick={async () => { setCoverBusy(true); try { await onGenerateCover(); } finally { setCoverBusy(false); } }}
                disabled={coverBusy} data-testid="community-admin-generate-cover"
                className="w-full flex items-center justify-center gap-1.5 border-2 border-cyan-400/70 bg-black text-cyan-300 rounded-lg py-2.5 font-mono text-[9px] font-black uppercase tracking-widest active:scale-95 disabled:opacity-60">
                {coverBusy ? <Loader2 size={12} className="animate-spin" /> : <Star size={12} />} Generate Cover · 720×720
              </button>
            )}
            <button onClick={onMove} data-testid="community-admin-move" className="w-full flex items-center justify-center gap-1.5 border-2 border-gold/60 bg-black text-gold rounded-lg py-2.5 font-mono text-[9px] font-black uppercase tracking-widest active:scale-95"><FolderInput size={12} /> Move to album</button>
            <div className="flex gap-2">
              <button onClick={onRename} data-testid="community-admin-rename" className="flex-1 flex items-center justify-center gap-1.5 border-2 border-white/25 bg-black text-white rounded-lg py-2.5 font-mono text-[9px] font-black uppercase tracking-widest active:scale-95"><Pencil size={12} /> Rename</button>
              <button onClick={onDelete} data-testid="community-admin-delete" className="flex-1 flex items-center justify-center gap-1.5 border-2 border-black bg-red-500 text-white rounded-lg py-2.5 font-mono text-[9px] font-black uppercase tracking-widest active:scale-95"><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* A titled album workspace — header + a 3-col thumbnail grid (or empty note). */
function AlbumSection({ testid, Icon, title, count, accent, items, onOpen, emptyNote,
                       selectMode, selected, onToggleSelect }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`} className="w-full flex items-center gap-1.5">
        <ChevronDown size={13} className="text-white/50 transition-transform duration-200" style={{ transform: open ? "none" : "rotate(-90deg)" }} />
        <Icon size={13} style={{ color: accent }} />
        <span className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-white">{title}</span>
        <span className="font-mono text-[9px] text-white/40">· {count}</span>
      </button>
      {open && (items.length === 0 ? (
        <p className="font-mono text-[9px] text-white/30 border border-dashed border-white/10 rounded-lg py-4 text-center">{emptyNote}</p>
      ) : (
        <div className="grid grid-cols-3 gap-1.5">
          {items.map((it) => {
            const sel = selected?.has(it.id);
            return (
              <button key={it.id} onClick={() => (selectMode ? onToggleSelect(it.id) : onOpen(it))} data-testid={`community-item-${it.id}`}
                className="relative aspect-square rounded-lg overflow-hidden border-2 transition-colors" style={{ borderColor: sel ? accent : `${accent}66` }}>
                <CommThumb item={it} />
                {it.track !== "image" && !selectMode && (
                  <span className="absolute inset-0 grid place-items-center bg-black/25"><Play size={20} className="text-white drop-shadow" fill="white" /></span>
                )}
                {selectMode && (
                  <span className="absolute top-1 right-1 z-10" data-testid={`community-select-${it.id}`}>
                    {sel ? <CheckSquare size={18} className="text-gold" fill="rgba(0,0,0,0.55)" /> : <Square size={18} className="text-white/80" />}
                  </span>
                )}
                <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent px-1.5 pt-4 pb-1">
                  <span className="block font-mono text-[7px] uppercase tracking-wide text-white/80 truncate">@{it.by}</span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function CommunityVault({ onUsed }) {
  const [tab, setTab] = useState("images");
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [using, setUsing] = useState(false);
  const [folders, setFolders] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newAlbum, setNewAlbum] = useState("");
  const [savingAlbum, setSavingAlbum] = useState(false);
  const [organizing, setOrganizing] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busyBulk, setBusyBulk] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null); // { ids: [] }
  const { isFounder } = useFounderGate();

  const active = TABS.find((t) => t.key === tab);

  const reloadVault = useCallback(() => {
    mistApi.communityVault().then(setData).catch(() => setData({ images: [], videos: [], audio: [] }));
  }, []);

  const aiOrganize = async () => {
    if (organizing) return;
    setOrganizing(true);
    const t = toast.loading("AI is sorting music, naming songs & clearing broken files…");
    try {
      const r = await mistApi.communityAiOrganize();
      toast.success(`Organized ✓ ${r.sorted} sorted · ${r.renamed} renamed · ${r.deleted} broken removed`, { id: t });
      reloadVault(); loadFolders(active.track);
    } catch (e) { toast.error(e?.response?.data?.detail || "Organize failed", { id: t }); }
    finally { setOrganizing(false); }
  };

  const renameItem = async () => {
    if (!preview) return;
    const nm = window.prompt("Rename this item", preview.title || preview.filename || "");
    if (nm == null) return;
    const name = nm.trim();
    if (!name) return;
    try {
      await mistApi.communityRename(preview.id, name);
      toast.success("Renamed ✓");
      setPreview((p) => (p ? { ...p, title: name } : p)); reloadVault();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  const deleteItem = async () => {
    if (!preview) return;
    if (!window.confirm("Delete this file from the Community Vault? This cannot be undone.")) return;
    try {
      await mistApi.communityDelete(preview.id);
      toast.success("Deleted ✓");
      setPreview(null); reloadVault();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const generateCover = async () => {
    if (!preview) return;
    try {
      const r = await mistApi.communityGenerateCover(preview.id);
      toast.success("Cover generated ✓");
      setPreview((p) => (p ? { ...p, cover_asset_id: r.cover_asset_id } : p)); reloadVault();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cover generation failed"); }
  };

  const loadFolders = useCallback((track) => {
    mistApi.listCommunityFolders(track).then((d) => setFolders(d.folders || [])).catch(() => setFolders([]));
  }, []);

  useEffect(() => {
    mistApi.communityVault().then(setData).catch(() => setData({ images: [], videos: [], audio: [] }));
  }, []);

  useEffect(() => { loadFolders(active.track); }, [tab, active.track, loadFolders]);

  const doUse = async () => {
    if (!preview) return;
    setUsing(true);
    try {
      const r = await mistApi.communityUse(preview.id);
      toast.success("Added to your library's Community Picks ✓");
      setPreview(null);
      onUsed?.(r.track);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't use this item"); }
    finally { setUsing(false); }
  };

  const createAlbum = async () => {
    const nm = newAlbum.trim();
    if (!nm) return;
    setSavingAlbum(true);
    try {
      const f = await mistApi.createCommunityFolder(nm, active.track);
      setFolders((cur) => (cur.some((x) => x.id === f.id) ? cur : [{ id: f.id, name: f.name, track: active.track }, ...cur]));
      setCreating(false); setNewAlbum("");
      toast.success(`Community album “${f.name}” created`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create album"); }
    finally { setSavingAlbum(false); }
  };

  const items = (data && data[tab]) || [];
  const selProps = {
    selectMode, selected,
    onToggleSelect: (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }),
  };
  const bulkDelete = async () => {
    if (!selected.size || busyBulk) return;
    if (!window.confirm(`Delete ${selected.size} file(s) from the Community Vault? This cannot be undone.`)) return;
    setBusyBulk(true);
    try {
      await Promise.all([...selected].map((id) => mistApi.communityDelete(id)));
      toast.success(`Deleted ${selected.size} ✓`);
      setSelected(new Set()); setSelectMode(false); reloadVault();
    } catch (e) { toast.error("Some deletes failed"); reloadVault(); }
    finally { setBusyBulk(false); }
  };
  const doMove = async (ids, folderId) => {
    if (!ids.length || busyBulk) return;
    setBusyBulk(true);
    try {
      await Promise.all(ids.map((id) => mistApi.communityMove(id, folderId)));
      toast.success(`Moved ${ids.length} ✓`);
      setMoveTarget(null); setSelected(new Set()); setSelectMode(false); setPreview(null); reloadVault();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
    finally { setBusyBulk(false); }
  };
  const labels = BLUEPRINT_LABELS[active.track] || BLUEPRINT_LABELS.image;
  const charItems = items.filter((it) => it.media_category === "character");
  const sceneItems = items.filter((it) => it.media_category === "scene");
  const unsorted = items.filter((it) => !it.community_folder_id && it.media_category !== "character" && it.media_category !== "scene");

  return (
    <div className="absolute inset-0 z-[45] bg-black flex flex-col" data-testid="community-vault">
      <div className="shrink-0 px-4 pt-4 pb-2">
        <p className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-[0.24em] text-gold"><Globe size={12} /> Community Vault</p>
        <h2 className="font-serif text-2xl text-white">Shared by everyone</h2>
        <p className="font-mono text-[9px] text-white/45 mt-0.5">Browse-only · tap any item to preview & reuse in your own creations</p>
        {isFounder && (
          <button onClick={aiOrganize} disabled={organizing} data-testid="community-ai-organize-btn"
            className="mt-2.5 w-full flex items-center justify-center gap-2 border-2 border-black bg-gold text-black rounded-lg py-2.5 font-mono text-[9px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
            {organizing ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} AI Organize &amp; Clean
          </button>
        )}
        {isFounder && (
          <button onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} data-testid="community-select-toggle"
            className="mt-2 w-full flex items-center justify-center gap-2 border-2 border-white/25 bg-black text-white rounded-lg py-2.5 font-mono text-[9px] font-black uppercase tracking-widest active:scale-95">
            <ListChecks size={13} /> {selectMode ? "Exit select" : "Select / bulk edit"}
          </button>
        )}
      </div>

      {/* Type tabs */}
      <div className="shrink-0 flex gap-2 px-4 pb-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`community-tab-${t.key}`}
            className="flex items-center gap-1.5 border-2 rounded-full px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest"
            style={tab === t.key ? { background: t.accent, borderColor: t.accent, color: "#041018" } : { borderColor: `${t.accent}66`, color: "#fff", background: "transparent" }}>
            <t.Icon size={12} /> {t.label} · {data ? (data[t.key] || []).length : "…"}
          </button>
        ))}
      </div>

      {/* Founder bulk-edit toolbar */}
      {isFounder && selectMode && (
        <div className="shrink-0 flex items-center gap-2 px-4 pb-2" data-testid="community-bulk-bar">
          <span className="font-mono text-[9px] text-white/70">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set(items.map((it) => it.id)))} data-testid="community-select-all" className="font-mono text-[9px] font-black uppercase tracking-widest text-gold">Select all</button>
          <button onClick={() => setSelected(new Set())} className="font-mono text-[9px] uppercase tracking-widest text-white/40">Clear</button>
          <div className="flex-1" />
          <button onClick={() => selected.size && setMoveTarget({ ids: [...selected] })} disabled={!selected.size || busyBulk} data-testid="community-bulk-move" className="flex items-center gap-1 border-2 border-gold/60 text-gold rounded-lg px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest disabled:opacity-40"><FolderInput size={12} /> Move</button>
          <button onClick={bulkDelete} disabled={!selected.size || busyBulk} data-testid="community-bulk-delete" className="flex items-center gap-1 border-2 border-black bg-red-500 text-white rounded-lg px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest disabled:opacity-40">{busyBulk ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete</button>
        </div>
      )}

      {/* Categorized album workspace */}
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-3 space-y-5" data-testid="community-grid">
        {data == null ? (
          <div className="grid place-items-center py-16"><Loader2 size={24} className="text-white/30 animate-spin" /></div>
        ) : (
          <>
            {/* Blueprint album 1 — R-System Character */}
            <AlbumSection testid="community-album-character" Icon={User} title={labels.character}
              count={charItems.length} accent={active.accent} items={charItems} onOpen={setPreview} {...selProps}
              emptyNote="Global actor profile assets appear here" />

            {/* Blueprint album 2 — R-System Scene */}
            <AlbumSection testid="community-album-scene" Icon={Clapperboard} title={labels.scene}
              count={sceneItems.length} accent={active.accent} items={sceneItems} onOpen={setPreview} {...selProps}
              emptyNote="Crowd-sourced environment / location assets appear here" />

            {/* Custom dynamic community albums */}
            <div className="space-y-3" data-testid="community-folders-sector">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-white/70 flex items-center gap-1.5"><Folder size={13} className="text-gold" /> Community Albums</span>
                {!creating && (
                  <button onClick={() => setCreating(true)} data-testid="community-new-album"
                    className="flex items-center gap-1 font-mono text-[9px] font-black uppercase tracking-widest text-gold hover:text-white">
                    <FolderPlus size={13} /> Save to New Album
                  </button>
                )}
              </div>

              {creating && (
                <div className="flex items-center gap-2" data-testid="community-new-album-row">
                  <input value={newAlbum} onChange={(e) => setNewAlbum(e.target.value)} data-testid="community-new-album-input"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createAlbum(); } }}
                    placeholder="New album name" autoFocus
                    className="flex-1 bg-black border-2 border-gold/60 outline-none px-3 py-2 text-sm text-white font-serif" />
                  <button onClick={createAlbum} disabled={savingAlbum || !newAlbum.trim()} data-testid="community-new-album-save"
                    className="shrink-0 border-2 border-gold bg-gold text-black px-2.5 py-2 disabled:opacity-50">
                    {savingAlbum ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  </button>
                  <button onClick={() => { setCreating(false); setNewAlbum(""); }} data-testid="community-new-album-cancel"
                    className="shrink-0 border-2 border-white/20 text-white/50 px-2.5 py-2"><X size={14} /></button>
                </div>
              )}

              {folders.length === 0 && !creating ? (
                <p className="font-mono text-[9px] text-white/30 border border-dashed border-white/10 rounded-lg py-4 text-center">No community albums yet — create the first one.</p>
              ) : (
                folders.map((f) => (
                  <AlbumSection key={f.id} testid={`community-folder-${f.id}`} Icon={Folder} title={f.name}
                    count={items.filter((it) => it.community_folder_id === f.id).length} accent={active.accent}
                    items={items.filter((it) => it.community_folder_id === f.id)} onOpen={setPreview} {...selProps}
                    emptyNote="No assets in this album yet" />
                ))
              )}
            </div>

            {/* Unsorted (legacy / no classification) */}
            {unsorted.length > 0 && (
              <AlbumSection testid="community-album-unsorted" Icon={Images} title="Unsorted"
                count={unsorted.length} accent={active.accent} items={unsorted} onOpen={setPreview} {...selProps}
                emptyNote="" />
            )}

            {items.length === 0 && folders.length === 0 && !creating && (
              <p className="font-mono text-[11px] text-white/45 text-center py-10">No {active.label.toLowerCase()} shared to the community yet.</p>
            )}
          </>
        )}
      </div>

      {preview && <PreviewModal item={preview} onClose={() => setPreview(null)} onUse={doUse} using={using} isFounder={isFounder} onRename={renameItem} onDelete={deleteItem} onMove={() => setMoveTarget({ ids: [preview.id] })} onGenerateCover={generateCover} />}

      {/* Founder move-to-album picker (used by bulk bar + preview) */}
      {moveTarget && (
        <div className="fixed inset-0 z-[210] bg-black/90 flex items-end sm:items-center justify-center p-4" data-testid="community-move-picker" onClick={() => setMoveTarget(null)}>
          <div className="w-full max-w-sm bg-[#0c0c0c] border-2 border-gold/40 rounded-2xl p-4 space-y-2" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[10px] font-black uppercase tracking-widest text-gold flex items-center gap-1.5"><FolderInput size={13} /> Move {moveTarget.ids.length} to album</p>
            <div className="max-h-[50vh] overflow-y-auto no-scrollbar space-y-1.5">
              {folders.map((f) => (
                <button key={f.id} onClick={() => doMove(moveTarget.ids, f.id)} disabled={busyBulk} data-testid={`community-move-to-${f.id}`}
                  className="w-full flex items-center gap-2 border-2 border-white/15 bg-black text-white rounded-lg px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest active:scale-95 disabled:opacity-50"><Folder size={13} className="text-gold" /> {f.name}</button>
              ))}
              <button onClick={() => doMove(moveTarget.ids, "")} disabled={busyBulk} data-testid="community-move-to-unsorted"
                className="w-full flex items-center gap-2 border-2 border-dashed border-white/20 text-white/70 rounded-lg px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest active:scale-95 disabled:opacity-50"><Images size={13} /> Unsorted (remove from album)</button>
              {folders.length === 0 && <p className="font-mono text-[9px] text-white/40 text-center py-3">No albums yet — create one first.</p>}
            </div>
            <button onClick={() => setMoveTarget(null)} className="w-full border-2 border-white/15 text-white/50 rounded-lg py-2 font-mono text-[9px] uppercase tracking-widest">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
